import { SearchResponse } from "@/lib/algolia";
import { useRouter } from "next/router";
import { useCallback, useMemo, useState } from "react";
import useSWRInfinite, { SWRInfiniteKeyLoader } from "swr/infinite";
import GridOutlineComponent from "@/components/GridOutline";
import HitsComponent from "./Hits";
import BadgesComponent from "./Badges";
import ToolbarComponent from "./Toolbar";
import { Container } from "./styles";
import LoadMoreComponent from "./LoadMore";
import HeadingComponent from "./Heading";
import { HeadingProps } from "./Heading/Heading";
import FiltersComponent, { FiltersProps } from "./Filters/Filters";
import TotalComponent from "./Total";
import PaginationComponent from "./Pagination";

// Cast to fix React 17 + @types/react 18 compatibility issue
const GridOutline = GridOutlineComponent as any;
const Hits = HitsComponent as any;
const Badges = BadgesComponent as any;
const Toolbar = ToolbarComponent as any;
const LoadMore = LoadMoreComponent as any;
const Heading = HeadingComponent as any;
const Filters = FiltersComponent as any;
const Total = TotalComponent as any;
const Pagination = PaginationComponent as any;

export type ProductListingProps = {
  bot: boolean;
  catalogs: HeadingProps["catalogs"];
  groups: FiltersProps["groups"];
};

const ProductListing: React.FC<ProductListingProps> = (props) => {
  const { catalogs, groups, bot } = props;
  const router = useRouter();
  const { query } = router;

  const getKey: SWRInfiniteKeyLoader = useCallback(
    (index) => ({
      ...query, // original query
      page: index, // current page
    }),
    [query]
  );

  const {
    data: pages,
    error,
    setSize,
    isValidating,
  } = useSWRInfinite<SearchResponse>(getKey);

  if (error) console.error(error);

  // Concatenates search hits across pages and uses
  // last entry for everything else
  const data = useMemo<SearchResponse | void>(() => {
    if (pages) {
      // Keep last fetched page as valid
      const [lastPage] = pages.slice(-1);
      // Concatenate hits from page to page
      let hits: SearchResponse["hits"] = [];
      hits = hits.concat(...pages?.map((page) => page.hits));
      return {
        ...lastPage,
        hits,
      };
    }
  }, [pages]);

  const { hits = [], page = 0, nbPages = 0, nbHits } = data || {};
  const canLoadMore = page + 1 < nbPages;
  const isLoadingMore = isValidating;
  const loadMore = () => setSize((size) => size + 1);

  const [filtersOpen, setFiltersOpen] = useState(false);
  const totalHits = nbHits || hits.length;

  return (
    <>
      <Container>
        <Heading catalogs={catalogs} />
        <Toolbar openFilters={() => setFiltersOpen(true)} />
        <Badges />
        <Total total={totalHits} />
        <Hits hits={hits} />
        {!bot && canLoadMore && (
          <LoadMore loadMore={loadMore} isLoadingMore={isLoadingMore} />
        )}
        {!!bot && <Pagination page={page} nbPages={nbPages} />}
        <GridOutline />
      </Container>
      <Filters
        isOpen={filtersOpen}
        handleClose={() => setFiltersOpen(false)}
        totalHits={totalHits}
        groups={groups}
      />
    </>
  );
};

export default ProductListing;
